import React, { useEffect } from "react";
import { reduxForm } from "redux-form";
import {
  constructDebitEpxRequest,
  isAmountPastDueBetweenBalanceAndPayoff,
} from "../../../utils/utils";
import { AuthRestartButton } from "../misc/restart-button.component";
import {
  isExcessPayment,
  ninetyDaysOverdueMessage,
  determinePayoffInfoOpenEnd,
  determinePayoffInfoClosedEnd,
  determinePayoffInfoEscrow,
  principalOnlyMessage,
  determineOneTimePaymentsByline,
  generateLoanDescription,
} from "../misc/messaging-utils";
import { AuthPaymentContainer } from "../misc/auth-payment-container.component";
import { AuthContinue } from "../misc/auth-continue.component";
import { AuthAmountField } from "./field-components/general/auth-amount-field.component";
import AuthDebitFields from "./field-components/general/auth-debit-fields.component";
import { formatOlbStyleAuthAmountValueFromBackend } from "../misc/auth-amount-utility";

const AuthPaymentDebitForm = (props) => {
  const hasSyncErrors = props.thisForm && props.thisForm.syncErrors;
  const inputAmount = props.thisForm && props.thisForm.values && props.thisForm.values.amount;

  const isPayoff = props.hasSelectedPayoff || props.amountPastDueBetweenPayoffAndBalance;
  const hasEscrow = props.selectedLoan.HasEscrowBalance;

  const shouldShowStandardMessaging = () => {
    return !props.isPrincipalOnly && !props.hasSelectedPayoff;
  };

  let payoffObject = {};

  if (!props.isPrincipalOnly) {
    if (hasEscrow) {
      payoffObject = determinePayoffInfoEscrow(inputAmount, props.PayoffAmount);
    } else {
      payoffObject = props.selectedLoan.IsOpenEndedLoan
        ? determinePayoffInfoOpenEnd(inputAmount, props.PayoffAmount, props.PrincipalBalance)
        : determinePayoffInfoClosedEnd(
            inputAmount,
            props.PrincipalBalance,
            props.TenDayPayoffAmount,
            props.amountPastDueBetweenPayoffAndBalance,
          );
    }
  }

  const excessMessage = isExcessPayment(
    inputAmount,
    props.selectedLoan.AmountDue,
    props.PrincipalBalance,
  );

  const amountIsPastDueIsBetweenBalanceAndPayoff = isAmountPastDueBetweenBalanceAndPayoff(
    props.selectedLoan.AmountPastDue,
    props.PayoffAmount,
    props.selectedLoan.CurrentBalance,
  );

  const bylinePastDue = amountIsPastDueIsBetweenBalanceAndPayoff
    ? props.PayoffAmount
    : props.selectedLoan.AmountPastDue;

  const byline = determineOneTimePaymentsByline(
    props.hasSelectedPayoff,
    bylinePastDue,
    props.PayoffAmount,
    props.selectedLoan.RegularPaymentAmount,
  );

  useEffect(() => {
    if (isPayoff) {
      props.change("amount", formatOlbStyleAuthAmountValueFromBackend(props.PayoffAmount));
    } else if (props.selectedLoan.AmountPastDue) {
      props.change(
        "amount",
        formatOlbStyleAuthAmountValueFromBackend(props.selectedLoan.AmountPastDue),
      );
    }
  }, []);

  // On submission, send a call to the payoff check endpoint
  const submitForm = () => {
    window.scrollTo(0, 0);

    const paymentDetails = constructDebitEpxRequest(props.thisForm.values);
    paymentDetails.IsPrincipalOnly = props.isPrincipalOnly;

    props.callAuthCheckPayoff(paymentDetails, "debit");
  };

  // If the call to payoff check has determined this payment is allowed,
  // send the next request
  useEffect(() => {
    if (props.authPayment.payoffCheck.PaymentIsAllowed) {
      const paymentDetails = constructDebitEpxRequest(props.thisForm.values);

      // Verify payment details endpoint
      props.callAuthSaveDebitInfo(paymentDetails);
    }
  }, [props.authPayment.payoffCheck]);

  useEffect(() => {
    // If the debit response has this property, that means there were no errors
    // saving the debit info and the debitResponse property has been set
    if (props.debitResponse.hasOwnProperty("AmountDue")) {
      props.setProcessStep(2);
    }
  }, [props.debitResponse]);

  return (
    <AuthPaymentContainer
      title="Payment Details"
      topChildren={[
        ninetyDaysOverdueMessage(
          props.selectedLoan.AmountPastDue,
          props.selectedLoan.Is90DaysOverdue,
          isPayoff
        ),
        principalOnlyMessage(props.isPrincipalOnly),
      ]}
    >
      <div className="bold f20">{generateLoanDescription(props.selectedLoan)}</div>
      <div className="f14">{byline}</div>
      <div className="my-4 divider"></div>
      <form onSubmit={props.handleSubmit(submitForm)}>
        <AuthAmountField
          isRecurring={false}
          isDebit={true}
          isPrincipalOnly={props.isPrincipalOnly}
          hasSelectedPayoff={isPayoff}
          currentBalance={props.selectedLoan.CurrentBalance}
          change={props.change}
          isOpenEnd={props.selectedLoan.IsOpenEndedLoan}
          payoffAmount={props.PayoffAmount}
          disabled={props.selectedLoan.Is90DaysOverdue || props.hasSelectedPayoff}
        />
        {/* Only show these messages for no-principal only payments */}
        {shouldShowStandardMessaging() && excessMessage}
        {shouldShowStandardMessaging() && payoffObject.component}
        <AuthDebitFields />
        <AuthContinue
          title="Continue"
          type="submit"
          disabled={props.submitting || hasSyncErrors || payoffObject.disabled}
        />
        <AuthRestartButton backToLoans={props.backToLoans} />
      </form>
    </AuthPaymentContainer>
  );
};

//Connect to Redux store
export const AuthPaymentDebit = reduxForm({
  form: "AuthPaymentDebit",
  destroyOnUnmount: false,
  initialValues: {
    amount: "",
    nameOnCard: "",
    account_nbr: "",
    exp_date: "",
    cvv2: "",
    zip_code: "",
  },
})(AuthPaymentDebitForm);



// WEBPACK FOOTER //
// ./src/components/authenticated-payments/payment-forms/auth-payment-debit-form.component.js